import {
  confirmActivityPrompt,
  getActivityStatus,
  onCsrPromptIssued,
  sendActivityHeartbeat,
  type ActivityPrompt,
} from '@/services/app/tenant/csr-activity';
import { ensureNotificationSocketConnected } from '@/services/app/websocket';
import { useAccess, useModel } from '@umijs/max';
import { Button, Modal } from 'antd';
import { ClockCircleOutlined, FieldTimeOutlined, MessageOutlined, SmileOutlined } from '@ant-design/icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';

const ACTIVITY_SLICE_MS = 5000;
const DEFAULT_HEARTBEAT_SECONDS = 60;

// Shared with the public tracking page's card design (soft pastel icon
// circle, navy heading, muted gray body) so this reads as the same product
// rather than a generic browser confirm dialog.
const NAVY = '#16335e';
const MUTED = '#5b6b80';

const REASON_CONFIG: Record<
  'random' | 'inactivity' | 'manual',
  { icon: React.ReactNode; color: string; softColor: string; title: string; body: (message: string | null) => string }
> = {
  random: {
    icon: <SmileOutlined style={{ fontSize: 38 }} />,
    color: '#1a6fe0',
    softColor: '#e8f1fc',
    title: 'Quick Check-In',
    body: () => "A quick check to confirm you're at your desk — these appear a few times during your shift.",
  },
  inactivity: {
    icon: <ClockCircleOutlined style={{ fontSize: 38 }} />,
    color: '#f5a623',
    softColor: '#fdf3df',
    title: 'Still There?',
    body: () => "We haven't seen any keyboard or mouse activity from you in the last 15 minutes.",
  },
  manual: {
    icon: <MessageOutlined style={{ fontSize: 38 }} />,
    color: '#1a6fe0',
    softColor: '#e8f1fc',
    title: 'A Message For You',
    body: (message) => message || "Please confirm you're at your desk.",
  },
};

// localStorage keys coordinating multiple app tabs of the same browser
// profile: only one tab may credit a given 5s slice, and a prompt confirmed
// in one tab closes the modal in the others.
const SLICE_KEY = 'csr_activity_last_slice';
const PROMPT_CONFIRMED_KEY = 'csr_activity_prompt_confirmed';

/**
 * Invisible productivity tracker mounted once in the app layout. For users
 * holding the "Allow Productivity Tracking" permission (the backend decides —
 * everyone else gets a single status call and nothing else) it:
 *
 * - counts keyboard/mouse events, but ONLY while this tab is visible and
 *   focused, so activity in unrelated browser tabs never counts;
 * - credits "active seconds" in 5s slices (a slice with at least one event
 *   is active), reported with a heartbeat every 60s;
 * - shows server-issued "confirm you're at your desk" prompts and reports
 *   the confirmation — either a random check-in, or a nudge the server
 *   fires after 15 minutes with no measured keyboard/mouse activity. Both
 *   are timed server-side, so neither can be anticipated or simulated.
 */
const CsrActivityTracker: React.FC = () => {
  const { initialState } = useModel('@@initialState');
  const currentUser = initialState?.currentUser;
  const firstName = (currentUser?.display_name || currentUser?.user_name || '').trim().split(' ')[0];

  const [enabled, setEnabled] = useState(false);
  const [prompt, setPrompt] = useState<
    { id: number; reason: 'random' | 'inactivity' | 'manual'; message: string | null; deadline: number } | null
  >(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [confirming, setConfirming] = useState(false);

  const keyboardCount = useRef(0);
  const mouseCount = useRef(0);
  const activeSlices = useRef(0);
  const lastSlice = useRef(-1);
  const lastBeatAt = useRef(Date.now());
  const heartbeatSeconds = useRef(DEFAULT_HEARTBEAT_SECONDS);

  const showPrompt = useCallback((incoming?: ActivityPrompt | null) => {
    if (!incoming) return;
    setPrompt((current) => {
      if (current?.id === incoming.id) return current;
      setSecondsLeft(incoming.seconds_remaining);
      return {
        id: incoming.id,
        reason: incoming.reason,
        message: incoming.message,
        deadline: Date.now() + incoming.seconds_remaining * 1000,
      };
    });
  }, []);

  // Users without the tracking permission never talk to the backend at all —
  // no status call, no listeners, no heartbeats. canBeProductivityTracked is
  // an explicit-grant check (no super-admin shortcut, see access.ts).
  const access = useAccess();
  const canBeTracked = currentUser?.user_type === 'tenant' && !!access.canBeProductivityTracked;

  // One status call decides everything; the backend re-checks the permission.
  useEffect(() => {
    if (!canBeTracked) return;
    let cancelled = false;
    getActivityStatus()
      .then((status) => {
        if (cancelled || !status?.tracking_enabled) return;
        heartbeatSeconds.current = status.heartbeat_interval || DEFAULT_HEARTBEAT_SECONDS;
        lastBeatAt.current = Date.now();
        setEnabled(true);
        showPrompt(status.prompt);
      })
      .catch(() => {
        // Tracking is best-effort; never disturb the app over it.
      });
    return () => {
      cancelled = true;
    };
  }, [canBeTracked, showPrompt]);

  // A manager-sent prompt is pushed over the websocket the instant it's
  // created, so it shows up immediately instead of waiting up to
  // heartbeat_interval seconds for the next poll to discover it. Random/
  // inactivity prompts don't need this — they're already returned by the
  // heartbeat that issues them. Best-effort: if the socket never connects
  // (not yet deployed, or this browser can't reach it), the next heartbeat
  // still picks the prompt up as before.
  useEffect(() => {
    if (!enabled) return;
    ensureNotificationSocketConnected();
    return onCsrPromptIssued((data) => {
      if (data) {
        showPrompt(data);
        return;
      }
      // Push arrived without a body (older server) — fall back to a fetch.
      getActivityStatus()
        .then((status) => showPrompt(status?.prompt))
        .catch(() => {
          // Ignore — the next heartbeat covers it regardless.
        });
    });
  }, [enabled, showPrompt]);

  // Input listeners + heartbeat loop.
  useEffect(() => {
    if (!enabled) return;

    const onActivity = (e: Event) => {
      // Ignore anything that happens while another tab/window has the user.
      if (document.visibilityState !== 'visible' || !document.hasFocus()) return;
      if (e.type === 'keydown') {
        keyboardCount.current += 1;
      } else {
        mouseCount.current += 1;
      }
      const slice = Math.floor(Date.now() / ACTIVITY_SLICE_MS);
      if (slice !== lastSlice.current) {
        lastSlice.current = slice;
        // The user can switch between two app tabs inside one slice; first
        // tab to stamp the slice in localStorage gets the credit.
        try {
          if (localStorage.getItem(SLICE_KEY) === String(slice)) return;
          localStorage.setItem(SLICE_KEY, String(slice));
        } catch {
          // Storage unavailable — count per-tab as before.
        }
        activeSlices.current += 1;
      }
    };

    const events: (keyof WindowEventMap)[] = ['keydown', 'click'];
    events.forEach((name) => window.addEventListener(name, onActivity, { capture: true, passive: true }));

    const beat = async () => {
      const now = Date.now();
      const intervalSeconds = Math.round((now - lastBeatAt.current) / 1000);
      if (intervalSeconds < 1) return;
      const payload = {
        interval_seconds: intervalSeconds,
        active_seconds: Math.min(activeSlices.current * (ACTIVITY_SLICE_MS / 1000), intervalSeconds),
        keyboard_events: keyboardCount.current,
        mouse_events: mouseCount.current,
      };
      try {
        const res = await sendActivityHeartbeat(payload);
        // Counters only reset on success — a failed beat keeps accumulating
        // and the next one covers the whole gap (server clamps the interval).
        lastBeatAt.current = now;
        keyboardCount.current = 0;
        mouseCount.current = 0;
        activeSlices.current = 0;
        if (res?.tracking_enabled === false) {
          setEnabled(false);
          return;
        }
        showPrompt(res?.prompt);
      } catch {
        // Network hiccup — retry on the next tick.
      }
    };

    const timer = window.setInterval(beat, heartbeatSeconds.current * 1000);
    return () => {
      events.forEach((name) => window.removeEventListener(name, onActivity, { capture: true }));
      window.clearInterval(timer);
    };
  }, [enabled, showPrompt]);

  // When another app tab confirms the prompt, close ours immediately instead
  // of letting the modal linger until its countdown lapses. 'storage' only
  // fires in the tabs that did NOT write the key.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== PROMPT_CONFIRMED_KEY || !e.newValue) return;
      const confirmedId = Number(e.newValue);
      setPrompt((current) => (current?.id === confirmedId ? null : current));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Prompt countdown; when it hits zero the window has lapsed and the server
  // records the prompt as missed on the next heartbeat.
  useEffect(() => {
    if (!prompt) return;
    const tick = window.setInterval(() => {
      const remaining = Math.max(Math.ceil((prompt.deadline - Date.now()) / 1000), 0);
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        setPrompt(null);
      }
    }, 1000);
    return () => window.clearInterval(tick);
  }, [prompt]);

  const onConfirm = async () => {
    if (!prompt) return;
    setConfirming(true);
    try {
      await confirmActivityPrompt(prompt.id);
      setPrompt(null);
      try {
        localStorage.setItem(PROMPT_CONFIRMED_KEY, String(prompt.id));
      } catch {
        // Best-effort cross-tab notice; other tabs fall back to the countdown.
      }
    } catch {
      // Leave the modal up; the CSR can press again within the window.
    } finally {
      setConfirming(false);
    }
  };

  if (!enabled) return null;

  const config = REASON_CONFIG[prompt?.reason ?? 'random'];
  const urgent = secondsLeft <= 15;

  return (
    <Modal
      open={!!prompt}
      title={null}
      footer={null}
      centered
      closable={false}
      maskClosable={false}
      keyboard={false}
      width={392}
      styles={{ body: { padding: '32px 28px 28px' } }}
    >
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            width: 88,
            height: 88,
            borderRadius: '50%',
            background: config.softColor,
            color: config.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
          }}
        >
          {config.icon}
        </div>
        {firstName && (
          <div style={{ fontSize: 14, fontWeight: 600, color: config.color, marginBottom: 4 }}>Hi {firstName},</div>
        )}
        <div style={{ fontSize: 20, fontWeight: 800, color: NAVY, marginBottom: 10 }}>{config.title}</div>
        <div style={{ fontSize: 15, color: MUTED, lineHeight: 1.55, marginBottom: 24 }}>
          {config.body(prompt?.message ?? null)}
        </div>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 14px',
            borderRadius: 999,
            fontSize: 13,
            fontWeight: 600,
            marginBottom: 22,
            background: urgent ? '#fdecea' : '#f5f7fa',
            color: urgent ? '#d4380d' : MUTED,
          }}
        >
          <FieldTimeOutlined /> {secondsLeft}s remaining
        </div>
        <Button
          type="primary"
          block
          size="large"
          loading={confirming}
          onClick={onConfirm}
          style={{ background: config.color, borderColor: config.color, fontWeight: 600, height: 44, borderRadius: 10 }}
        >
          I&apos;m Here
        </Button>
      </div>
    </Modal>
  );
};

export default CsrActivityTracker;
